import { useId } from "react";

import styles from "./tooth-surfaces.module.css";
import { toothType } from "./tooth-geometry";

/**
 * **The tooth seen from above** — a square cut into five parts: the biting
 * surface in the middle and the four sides around it. Drawn small in front of
 * every tooth on the chart, and large in the tooth's panel, where the dentist
 * picks the parts a procedure touched.
 *
 * The stored letters are FDI's five, M, O, D, B and L (`teeth.dto.ts`). Only
 * the words change with the tooth (TS-D3): the middle of a front tooth is its
 * incisal edge, the outer side of a front tooth is facial, and the inner side
 * of an upper tooth is palatal — M, O/I, D, B/F, L/P. Facial rather than
 * labial, because on a lower front tooth labial and lingual would both be "L".
 * The letter stored stays what the API accepts.
 */

export type Surface = "M" | "O" | "D" | "B" | "L";

export const SURFACE_ORDER: readonly Surface[] = ["M", "O", "D", "B", "L"];

type Side = "top" | "right" | "bottom" | "left" | "centre";

/**
 * Which side of the square each surface is on, as the chart is read — the
 * patient's right on the left of the screen, the upper arch above.
 *
 *  - **Buccal faces out of the mouth**: up on the upper arch, down on the
 *    lower, so the inner side always faces the bite line between the rows.
 *  - **Mesial faces the middle of the arch**: right for the teeth on the
 *    chart's left half (quadrants 1, 4, 5, 8), left for the others.
 */
export function sideOf(surface: Surface, fdiNumber: number): Side {
  const quadrant = Math.floor(fdiNumber / 10);
  const isUpper = [1, 2, 5, 6].includes(quadrant);
  const onLeftHalf = [1, 4, 5, 8].includes(quadrant);
  switch (surface) {
    case "O":
      return "centre";
    case "B":
      return isUpper ? "top" : "bottom";
    case "L":
      return isUpper ? "bottom" : "top";
    case "M":
      return onLeftHalf ? "right" : "left";
    case "D":
      return onLeftHalf ? "left" : "right";
  }
}

/** The surface's name on this tooth — the words a dentist uses for it. */
export function surfaceName(surface: Surface, fdiNumber: number): string {
  const front = ["incisor", "canine"].includes(toothType(fdiNumber));
  const isUpper = [1, 2, 5, 6].includes(Math.floor(fdiNumber / 10));
  switch (surface) {
    case "M":
      return "Mesial";
    case "D":
      return "Distal";
    case "O":
      return front ? "Incisal" : "Occlusal";
    case "B":
      return front ? "Facial" : "Buccal";
    case "L":
      return isUpper ? "Palatal" : "Lingual";
  }
}

/** The short letter shown beside a procedure — I for incisal, P for palatal. */
export function surfaceLetter(surface: Surface, fdiNumber: number): string {
  return surfaceName(surface, fdiNumber).charAt(0);
}

/**
 * The five parts in a 40-unit square: four trapezoids meeting a centre square.
 * The sides bow slightly toward the centre, as the sketch drew them, so the
 * shape reads as a tooth from above rather than as a picture frame.
 */
const SHAPE: Record<Side, string> = {
  top: "M2 2 L38 2 Q30 6 27 13 L13 13 Q10 6 2 2 Z",
  right: "M38 2 L38 38 Q30 34 27 27 L27 13 Q30 6 38 2 Z",
  bottom: "M38 38 L2 38 Q10 34 13 27 L27 27 Q30 34 38 38 Z",
  left: "M2 38 L2 2 Q10 6 13 13 L13 27 Q10 34 2 38 Z",
  centre: "M13 13 L27 13 L27 27 L13 27 Z",
};

export interface SurfaceSquareProps {
  fdiNumber: number;
  /** Parts completed work has touched — drawn blue. */
  worked?: readonly string[];
  /**
   * Parts planned work names — drawn in the chart's to-be-done red. A part in
   * both lists is red: there is still work waiting on it.
   */
  planned?: readonly string[];
  /** Parts chosen for the procedure about to be recorded. */
  chosen?: readonly string[];
  /** Given, each part is pressable and calls this; absent, the square only draws. */
  onToggle?: (surface: Surface) => void;
  disabled?: boolean;
  className?: string;
}

export function SurfaceSquare({
  fdiNumber,
  worked = [],
  planned = [],
  chosen = [],
  onToggle,
  disabled = false,
  className,
}: SurfaceSquareProps): React.JSX.Element {
  const pressable = onToggle !== undefined;
  // One stripe pattern per square: an id must be unique in the page, and a
  // chart draws fifty squares. useId's colons are not safe inside url(#…).
  const stripesId = `surface-stripes-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <svg
      className={[styles.square, pressable && styles.pressable, className]
        .filter(Boolean)
        .join(" ")}
      viewBox="0 0 40 40"
      // The small one on the chart is read with the tooth's own label.
      aria-hidden={pressable ? undefined : true}
      role={pressable ? "group" : undefined}
      aria-label={pressable ? `Parts of tooth ${String(fdiNumber)}` : undefined}
      focusable="false"
      data-testid={pressable ? "surface-picker" : "surface-square"}
    >
      {/*
        **Planned parts are striped** (TS-D5), not only red: the chart never
        tells two states apart by hue alone (UI-TC3), and solid red against
        solid blue would. Stripes are the second channel, as the cross-hatch
        is for a removed tooth.
      */}
      <defs>
        <pattern
          id={stripesId}
          width="7"
          height="7"
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect className={styles.stripeGround} width="7" height="7" />
          <rect className={styles.stripe} width="3.5" height="7" />
        </pattern>
      </defs>
      {SURFACE_ORDER.map((surface) => {
        const isPlanned = planned.includes(surface);
        const isWorked = !isPlanned && worked.includes(surface);
        const isChosen = chosen.includes(surface);
        const name = surfaceName(surface, fdiNumber);
        return (
          <path
            key={surface}
            d={SHAPE[sideOf(surface, fdiNumber)]}
            className={[
              styles.part,
              isWorked && styles.worked,
              isPlanned && styles.planned,
              isChosen && styles.chosen,
            ]
              .filter(Boolean)
              .join(" ")}
            // The stripes are a fill, set here because the id is per square.
            style={isPlanned && !isChosen ? { fill: `url(#${stripesId})` } : undefined}
            data-surface={surface}
            data-worked={isWorked ? "" : undefined}
            data-planned={isPlanned ? "" : undefined}
            data-chosen={isChosen ? "" : undefined}
            {...(pressable
              ? {
                  role: "checkbox",
                  tabIndex: disabled ? -1 : 0,
                  "aria-checked": isChosen,
                  "aria-disabled": disabled || undefined,
                  "aria-label": `${name}${
                    isPlanned ? ", to be done" : isWorked ? ", worked on" : ""
                  }`,
                  onClick: () => {
                    if (!disabled) onToggle(surface);
                  },
                  onKeyDown: (event: React.KeyboardEvent) => {
                    if (disabled || (event.key !== " " && event.key !== "Enter")) return;
                    event.preventDefault();
                    onToggle(surface);
                  },
                }
              : {})}
          >
            {pressable && <title>{name}</title>}
          </path>
        );
      })}
    </svg>
  );
}
